import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ICloseItemParams,
  ICreateItemParams,
  Item,
  IUpdateItemParams,
  OpportunitiesSort,
  Stage,
} from '../api/opportunitiesApi/interfaces';
import {
  closeItem,
  createItem,
  deleteItem,
  getItem,
  getOpportunities,
  getStages,
  updateItem,
} from '../api/opportunitiesApi';
import { AxiosError } from 'axios';
import { IPaginatedReactQueryParams } from './interfaces';
import { FilterFields } from '../components/ManagedDataTable/interfaces';

export const useGetStages = (options?: {
  includeItems?: boolean;
  filters?: FilterFields;
  enabled?: boolean;
}) => {
  return useQuery<Stage[]>({
    queryKey: [
      'opportunity-stages',
      {
        includeItems: options?.includeItems,
        filters: options?.filters,
      },
    ],
    queryFn: () =>
      getStages({
        includeItems: options?.includeItems,
        filters: options?.filters,
      }),
    enabled: options?.enabled ?? true,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useUpdateItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    number,
    AxiosError,
    { itemId: number; itemData: IUpdateItemParams }
  >({
    mutationFn: async ({ itemId, itemData }) => updateItem(itemId, itemData),
    onSuccess: (itemId: number) => {
      queryClient.invalidateQueries({
        queryKey: ['opportunity-item', itemId],
      });
    },
  });
};

export const useGetItem = (id: number) => {
  return useQuery<Item>({
    queryFn: () => getItem(id),
    queryKey: ['opportunity-item', id],
    enabled: !!id,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if ((error as AxiosError)?.response?.status === 404) {
        return false;
      }

      return failureCount < 3;
    },
  });
};

export const useCloseItem = () => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    AxiosError,
    { itemId: number; closeData: ICloseItemParams }
  >({
    mutationFn: async ({ itemId, closeData }) => closeItem(itemId, closeData),
    onSuccess: (_, { itemId }) => {
      queryClient.invalidateQueries({
        queryKey: ['opportunity-item', itemId],
      });
    },
  });
};

export const useCreateItemMutation = () => {
  return useMutation<number, AxiosError, ICreateItemParams>({
    mutationFn: createItem,
  });
};

export const useDeleteItem = () => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, number>({
    mutationFn: deleteItem,
    onSuccess: (_, itemId) => {
      queryClient.removeQueries(['opportunity-item', itemId]);
    },
  });
};

export const useGetOpportunities = ({
  page,
  pageSize,
  sortField,
  search,
  filter,
}: IPaginatedReactQueryParams<OpportunitiesSort>) => {
  return useQuery({
    queryKey: ['opportunities', page, pageSize, sortField, search, filter],
    queryFn: () => {
      return getOpportunities(
        page + 1,
        pageSize,
        sortField,
        search || undefined,
        filter
      );
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};
